import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, money } from "@/lib/api";
import { ProductFormDialog } from "@/components/ProductFormDialog";
import { DiscountFormDialog } from "@/components/DiscountFormDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { PrintfulSettings, PrintfulOrderCell } from "@/components/PrintfulAdmin";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const th = "text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500 font-normal py-3 px-3";
const td = "py-3 px-3 border-t border-white/5 text-sm";

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/discounts").then((r) => setDiscounts(r.data));
  }, []);
  useEffect(load, [load]);

  const remove = async () => {
    try {
      if (toDelete.code) { await api.delete(`/admin/discounts/${toDelete.id}`); toast.success("Discount deleted"); }
      else { await api.delete(`/admin/products/${toDelete.id}`); toast.success("Product deleted"); }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setToDelete(null);
  };

  const toggleDiscount = async (d) => {
    const body = { ...d, active: !d.active }; delete body.id; delete body.uses; delete body.created_at;
    try { await api.put(`/admin/discounts/${d.id}`, body); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/orders/${id}`, { status }); toast.success("Order updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const shown = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold mb-2">Control room</p>
      <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl">Admin</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-8">
        {[["Revenue", stats && money(stats.revenue), "stat-revenue"], ["Paid orders", stats?.orders, "stat-orders"], ["Products", stats?.products, "stat-products"], ["Low stock", stats?.low_stock, "stat-low-stock"]].map(([l, v, id]) => (
          <div key={l} className="bg-[#09090B] p-6">
            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{l}</p>
            <p data-testid={id} className="font-display font-black text-3xl mt-1">{v ?? "—"}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products" className="mt-10">
        <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start p-0 h-auto">
          {["products", "orders", "discounts", "fulfillment"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="rounded-none font-display uppercase font-bold tracking-wider px-6 py-3 data-[state=active]:bg-transparent data-[state=active]:text-[#FF3B30] data-[state=active]:border-b-2 data-[state=active]:border-[#FF3B30]">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-between gap-3 mb-4">
            <input data-testid="admin-product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter products..." className="h-10 w-64 bg-white/5 border border-white/15 focus:border-[#FF3B30] outline-none px-3 text-sm" />
            <button data-testid="admin-add-product-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="h-10 px-5 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-bold tracking-wider flex items-center gap-2"><Plus className="w-4 h-4" />Add product</button>
          </div>
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full min-w-[720px]">
              <thead><tr><th className={th}>Product</th><th className={th}>Category</th><th className={th}>Price</th><th className={th}>Stock</th><th className={th}></th></tr></thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="hover:bg-white/[0.03]">
                    <td className={td}><div className="flex items-center gap-3"><img src={p.image} alt="" className="w-10 h-12 object-cover" /><span className="font-semibold">{p.name}</span>{p.featured && <span className="font-mono text-[9px] text-[#FF3B30] border border-[#FF3B30]/40 px-1">FEATURED</span>}</div></td>
                    <td className={`${td} font-mono text-xs uppercase text-neutral-400`}>{p.category}</td>
                    <td className={`${td} font-mono`}>{money(p.price)}</td>
                    <td className={`${td} font-mono ${p.stock <= 5 ? "text-[#FF9500]" : ""}`}>{p.stock}</td>
                    <td className={`${td} text-right whitespace-nowrap`}>
                      <button data-testid={`admin-edit-${p.id}`} onClick={() => { setEditing(p); setFormOpen(true); }} className="p-2 hover:bg-white/10 hover:text-white"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`admin-delete-${p.id}`} onClick={() => setToDelete(p)} className="p-2 hover:bg-[#FF3B30]/20 hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full min-w-[1000px]">
              <thead><tr><th className={th}>Order</th><th className={th}>Customer</th><th className={th}>Items</th><th className={th}>Total</th><th className={th}>Payment</th><th className={th}>Status</th><th className={th}>Printful</th></tr></thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan={7} className={`${td} text-neutral-500`} data-testid="admin-orders-empty">No orders yet.</td></tr>}
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`admin-order-row-${o.id}`}>
                    <td className={`${td} font-mono text-xs`}>#{o.id.slice(-8).toUpperCase()}<div className="text-neutral-500">{new Date(o.created_at).toLocaleDateString()}</div></td>
                    <td className={`${td} text-neutral-300`}>{o.user_email}{o.shipping_address?.city && <div className="text-xs text-neutral-500">{o.shipping_address.city}, {o.shipping_address.country}</div>}</td>
                    <td className={`${td} text-xs text-neutral-400`}>{o.items.map((i, idx) => <div key={idx}>{i.quantity}× {i.name}{i.customization && <span className="text-neutral-500"> — {i.customization.summary}</span>}</div>)}</td>
                    <td className={`${td} font-mono`}>{money(o.total)}</td>
                    <td className={td}><StatusBadge status={o.payment_status === "paid" ? "paid" : o.payment_status} /></td>
                    <td className={td}>
                      <select data-testid={`admin-order-status-${o.id}`} value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="bg-white/5 border border-white/15 h-9 px-2 text-xs font-mono uppercase">
                        {["pending", "paid", "shipped", "delivered", "cancelled"].map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
                      </select>
                    </td>
                    <td className={td}><PrintfulOrderCell order={o} onChanged={load} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="fulfillment" className="mt-6">
          <PrintfulSettings />
        </TabsContent>

        <TabsContent value="discounts" className="mt-6">
          <div className="flex justify-end mb-4">
            <button data-testid="admin-add-discount-button" onClick={() => { setEditingDiscount(null); setDiscountOpen(true); }} className="h-10 px-5 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-bold tracking-wider flex items-center gap-2"><Plus className="w-4 h-4" />New code</button>
          </div>
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full min-w-[820px]">
              <thead><tr><th className={th}>Code</th><th className={th}>Discount</th><th className={th}>Min. order</th><th className={th}>Uses</th><th className={th}>Expires</th><th className={th}>Active</th><th className={th}></th></tr></thead>
              <tbody>
                {discounts.length === 0 && <tr><td colSpan={7} className={`${td} text-neutral-500`} data-testid="admin-discounts-empty">No discount codes yet.</td></tr>}
                {discounts.map((d) => (
                  <tr key={d.id} data-testid={`admin-discount-row-${d.id}`} className="hover:bg-white/[0.03]">
                    <td className={`${td} font-mono font-bold`}>{d.code}</td>
                    <td className={`${td} font-mono`}>{d.type === "percent" ? `${d.value}%` : money(d.value)} off</td>
                    <td className={`${td} font-mono text-neutral-400`}>{d.min_subtotal ? money(d.min_subtotal) : "—"}</td>
                    <td className={`${td} font-mono`}>{d.uses}{d.max_uses ? ` / ${d.max_uses}` : ""}</td>
                    <td className={`${td} font-mono text-xs text-neutral-400`}>{d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "Never"}</td>
                    <td className={td}>
                      <button data-testid={`admin-discount-toggle-${d.id}`} onClick={() => toggleDiscount(d)} className={`font-mono text-[10px] uppercase px-2 py-1 border ${d.active ? "text-emerald-400 border-emerald-400/40" : "text-neutral-500 border-white/15"}`}>{d.active ? "Active" : "Paused"}</button>
                    </td>
                    <td className={`${td} text-right whitespace-nowrap`}>
                      <button data-testid={`admin-discount-edit-${d.id}`} onClick={() => { setEditingDiscount(d); setDiscountOpen(true); }} className="p-2 hover:bg-white/10 hover:text-white"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`admin-discount-delete-${d.id}`} onClick={() => setToDelete(d)} className="p-2 hover:bg-[#FF3B30]/20 hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <ProductFormDialog open={formOpen} onOpenChange={setFormOpen} product={editing} onSaved={load} />
      <DiscountFormDialog open={discountOpen} onOpenChange={setDiscountOpen} discount={editingDiscount} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="bg-[#121215] border-white/10 rounded-none">
          <AlertDialogHeader><AlertDialogTitle className="font-display uppercase font-black text-2xl">Delete {toDelete?.name || toDelete?.code}?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={remove} className="rounded-none bg-[#FF3B30] hover:bg-[#E02E24]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
